import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useAnalytics } from '../contexts/AnalyticsContext';
import { subDays, isAfter, differenceInMinutes } from 'date-fns';

const { FiUsers, FiEye, FiClock, FiTrendingUp } = FiIcons;

const MetricsCards = ({ timeRange }) => {
  const { analyticsData } = useAnalytics();

  const metrics = useMemo(() => {
    const now = new Date();
    const days = timeRange === '24h' ? 1 : parseInt(timeRange) || 7;
    const cutoff = subDays(now, days);
    
    const filteredViews = (analyticsData.pageViews || []).filter(pv => 
      isAfter(new Date(pv.timestamp), cutoff)
    );

    const uniqueVisitors = new Set(filteredViews.map(pv => pv.visitorId)).size;
    const sessionIds = new Set(filteredViews.map(pv => pv.sessionId));
    
    // Calculate Avg Duration (Simplified)
    let totalMinutes = 0;
    sessionIds.forEach(sid => {
      const sViews = filteredViews.filter(v => v.sessionId === sid).sort((a,b) => a.timestamp - b.timestamp);
      if (sViews.length > 1) {
        totalMinutes += (sViews[sViews.length-1].timestamp - sViews[0].timestamp) / 60000;
      }
    });
    const avgDuration = sessionIds.size > 0 ? (totalMinutes / sessionIds.size).toFixed(1) : 0;

    return [
      { title: 'Total Page Views', value: filteredViews.length.toLocaleString(), icon: FiEye, color: '#673ab7', bg: '#ede7f6' },
      { title: 'Unique Visitors', value: uniqueVisitors.toLocaleString(), icon: FiUsers, color: '#2196f3', bg: '#e3f2fd' },
      { title: 'Active Sessions', value: sessionIds.size.toLocaleString(), icon: FiTrendingUp, color: '#00c853', bg: '#b9f6ca44' },
      { title: 'Avg. Duration', value: `${avgDuration}m`, icon: FiClock, color: '#f44336', bg: '#ef9a9a44' }
    ];
  }, [analyticsData.pageViews, timeRange]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
      {metrics.map((metric, index) => (
        <motion.div key={metric.title} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-between">
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{metric.title}</p>
            <h3 className="text-2xl font-black text-slate-900">{metric.value}</h3>
          </div>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: metric.bg, color: metric.color }}>
            <SafeIcon icon={metric.icon} className="text-xl" />
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default MetricsCards;